import API from '@/axios/API';
import CustomButton from '@/components/Common/Buttons/CustomButton';
import { DataContext } from '@/providers/DataProviders/DataProvider';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider/GlobalSnackbarProvider';
import { NotificationContext } from '@/providers/Notifications/NotificationProvider';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
    Avatar,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    MenuItem,
    TextareaAutosize,
    TextField,
    Typography
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { settings, kb } from '../../../../api';
import ROUTES from '../../../../routes/ROUTES';
import ArticleIcon from '@mui/icons-material/Article';
import DescriptionIcon from '@mui/icons-material/Description';

const CreateTicketModal = ({ open, onClose, onSubmit }) => {
    const { fetchNotificationData, notificationStats } = useContext(NotificationContext);
    const { userId, setActive } = useContext(DataContext);
    const loginCustomerId = localStorage.getItem("loginCustomerId");
    const { showSnackbar } = useSnackbar();

    // Form states
    const [step, setStep] = useState(1);
    const [issueCategories, setIssueCategories] = useState([]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const data = await settings.listIssueCategories();
                const sortedCategories = (data || []).map(cat => ({
                    ...cat,
                    subCategories: cat.subCategories
                        ? [...cat.subCategories].sort((a, b) => (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' }))
                        : []
                })).sort((a, b) => (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' }));
                setIssueCategories(sortedCategories);
            } catch (err) {
                console.error('Failed to fetch categories:', err);
            }
        };
        fetchCategories();
    }, []);

    const [category, setCategory] = useState('');
    const [issue, setIssue] = useState('');
    const [categoryElseText, setCategoryElseText] = useState('');

    const selectedCategory = issueCategories.find(c => c.id === category);
    const subCategories = selectedCategory?.subCategories || [];

    const [notSpecificToOrder, setNotSpecificToOrder] = useState(false);
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                const id = loginCustomerId || userId;
                if (!id) return;
                const response = await API.get(`${import.meta.env.VITE_BACKEND_API}/order/recent?customerId=${id}`);
                if (response.data && response.data.data) {
                    setOrders(response.data.data);
                }
            } catch (err) {
                console.error('Failed to fetch recent orders:', err);
            }
        };
        fetchOrders();
    }, [loginCustomerId, userId]);

    const [description, setDescription] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [suggestedSolutions, setSuggestedSolutions] = useState([]);
    const [fetchingSolutions, setFetchingSolutions] = useState(false);

    useEffect(() => {
        if (step === 4) {
            const fetchSuggestions = async () => {
                setFetchingSolutions(true);
                try {
                    let query = '';
                    const selectedSubCat = subCategories.find(s => s.id === issue);
                    if (selectedSubCat?.name?.toLowerCase() === 'something else') {
                        query = categoryElseText || '';
                    } else {
                        query = selectedSubCat?.name || selectedCategory?.name || '';
                    }

                    const response = await kb.listArticles(undefined, query);
                    const articles = Array.isArray(response)
                        ? response
                        : Array.isArray(response?.content)
                            ? response.content
                            : Array.isArray(response?.data)
                                ? response.data
                                : [];
                    setSuggestedSolutions(articles.slice(0, 5));
                } catch (err) {
                    console.error("Failed to fetch suggestions:", err);
                    setSuggestedSolutions([]);
                } finally {
                    setFetchingSolutions(false);
                }
            };
            fetchSuggestions();
        }
    }, [step, category, issue, categoryElseText, selectedCategory, subCategories]);

    // File handlers
    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        const newAttachments = files.map(file => ({
            file,
            preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
        }));
        setAttachments(prev => [...prev, ...newAttachments]);
    };
    const handleRemoveAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const toggleOrder = (id) => {
        if (selectedOrders.includes(id)) {
            setSelectedOrders(selectedOrders.filter(o => o !== id));
        } else {
            setSelectedOrders([...selectedOrders, id]);
            setNotSpecificToOrder(false);
        }
    };

    const handleNotSpecificChange = (e) => {
        const checked = e.target.checked;
        setNotSpecificToOrder(checked);
        if (checked) {
            setSelectedOrders([]);
        }
    };

    const getOrderLabel = () => {
        if (notSpecificToOrder) return "Not specific to an order";
        if (selectedOrders.length === 0) return "None";
        return selectedOrders.map(id => {
            const order = orders.find(o => o.orderNumber === id);
            return order ? `${order.orderNumber} · ${order.organisationName}` : id;
        }).join(', ');
    };

    // Validations
    const isStep1Valid = category && category.trim() !== '';
    const isStep2Valid = issue && issue.trim() !== '';
    const isStep3Valid = notSpecificToOrder || selectedOrders.length > 0;
    const isStep4Valid = description && description.trim() !== '';

    // Submit
    const handleSubmit = async () => {
        if (!isStep4Valid) return;
        setLoading(true);

        try {
            // If there's file attachments, upload them first to get CDN URLs
            let attachmentsPayload = [];
            if (attachments.length > 0) {
                const uploadPromises = attachments.map(async ({ file }) => {
                    try {
                        const formData = new FormData();
                        formData.append('file', file);
                        const uploadResp = await API.post(
                            `${import.meta.env.VITE_BACKEND_API}/uploadImages?key=${file.name}`,
                            formData,
                            { headers: { 'Content-Type': 'multipart/form-data' } }
                        );
                        if (uploadResp.status === 200 && uploadResp.data.fileUrl) {
                            return uploadResp.data.fileUrl;
                        }
                    } catch (uploadErr) {
                        console.error('File upload failed:', uploadErr);
                    }
                    return null;
                });
                const results = await Promise.all(uploadPromises);
                attachmentsPayload = results.filter(r => r !== null);
            }

            const selectedSubCategory = subCategories.find(s => s.id === issue);
            const isElseSub = selectedSubCategory?.name?.toLowerCase() === 'something else';
            const ticketData = {
                // Fields matching POST /api/v1/workspace/{workspaceId}/support/ticket
                requesterId: loginCustomerId || userId,
                subject: `${selectedCategory?.name || 'Support'} - ${selectedSubCategory?.name || issue}`,
                description: description,
                issueCategoryId: category,
                issueSubCategoryId: issue,
                customerId: loginCustomerId,
                channel: 'IN_APP',
                orderIds: notSpecificToOrder ? [] : selectedOrders.map((order) => order.toString()),
                attachments: attachmentsPayload,
            };

            const result = await onSubmit(ticketData);

            if (result.success) {
                resetForm();
                onClose();
                fetchNotificationData();
                notificationStats();
            } else {
                showSnackbar(result.error || 'Failed to create ticket', 'error');
            }
        } catch (error) {
            console.error('Error creating ticket:', error);
            showSnackbar('Failed to create ticket', 'error');
        } finally {
            setLoading(false);
            fetchNotificationData();
        }
    };

    // Reset form
    const resetForm = () => {
        setStep(1);
        setCategory('');
        setIssue('');
        setCategoryElseText('');
        setNotSpecificToOrder(false);
        setSelectedOrders([]);
        setDescription('');
        setAttachments([]);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };


    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
                    overflow: 'hidden',
                }
            }}
            sx={{
                '& .MuiBackdrop-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    backdropFilter: 'blur(4px)',
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 3,
                    py: 2.5,
                    borderBottom: '1px solid #E0E0E0',
                }}
            >
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        fontSize: '1.5rem',
                        color: '#1a1a1a',
                    }}
                >
                    Create new ticket
                </Typography>
                <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{
                        color: '#616161',
                        borderRadius: 2,
                        transition: 'all 0.2s ease-in-out',
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 3, py: 3, overflowX: 'hidden', overflowY: 'auto', maxHeight: '70vh' }}>
                <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
                    <Typography sx={{ color: 'rgba(74, 102, 241, 1)', fontWeight: 700, fontSize: '0.75rem', letterSpacing: 1, mb: 1 }}>
                        STEP {step} OF 4
                    </Typography>

                    <Box sx={{ width: '100%', mb: 1 }}>

                        {/* STEP 1 */}
                        {step === 1 && (
                            <Box sx={{
                                width: '100%', px: '2px',
                                '@keyframes fadeSlideIn': {
                                    '0%': { opacity: 0, transform: 'translateY(14px)' },
                                    '100%': { opacity: 1, transform: 'translateY(0)' },
                                },
                                animation: 'fadeSlideIn 0.35s cubic-bezier(0.4, 0, 0.2, 1) both',
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                    What do you need help with?
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#616161', mb: 3 }}>
                                    Pick the area closest to your issue so we route it to the right team.
                                </Typography>

                                <TextField
                                    select
                                    fullWidth
                                    value={category}
                                    onChange={(e) => {
                                        setCategory(e.target.value);
                                        setIssue(""); // Reset subcategory

                                        setTimeout(() => {
                                            setStep((prev) => (prev === 1 ? 2 : prev));
                                        }, 400);
                                    }}
                                    SelectProps={{
                                        displayEmpty: true,
                                        renderValue: (selected) => {
                                            if (!selected) return <em style={{ color: '#9e9e9e' }}>Select category</em>;
                                            const cat = issueCategories.find(c => c.id === selected);
                                            return cat ? cat.name : selected;
                                        }
                                    }}
                                    sx={{
                                        '.MuiSelect-select': { py: 1.5 },
                                        '& .MuiOutlinedInput-root': { borderRadius: 2 }
                                    }}
                                >
                                    <MenuItem disabled value="">
                                        <em>Select category</em>
                                    </MenuItem>
                                    {issueCategories.map((cat) => (
                                        <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                                    ))}
                                </TextField>


                            </Box>
                        )}

                        {/* STEP 2 */}
                        {step === 2 && (
                            <Box sx={{
                                width: '100%', px: '2px',
                                '@keyframes fadeSlideIn': {
                                    '0%': { opacity: 0, transform: 'translateY(14px)' },
                                    '100%': { opacity: 1, transform: 'translateY(0)' },
                                },
                                animation: 'fadeSlideIn 0.35s cubic-bezier(0.4, 0, 0.2, 1) both',
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                    Which issue best describes you?
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#616161', mb: 3 }}>
                                    Select the specific problem you are facing.
                                </Typography>

                                <TextField
                                    select
                                    fullWidth
                                    value={issue}
                                    onChange={(e) => {
                                        setIssue(e.target.value);

                                        setTimeout(() => {
                                            setStep((prev) => (prev === 2 ? 3 : prev));
                                        }, 400);
                                    }}
                                    SelectProps={{
                                        displayEmpty: true,
                                        renderValue: (selected) => {
                                            // Nothing selected
                                            if (!selected) {
                                                return <em style={{ color: '#9e9e9e' }}>Select subcategory</em>;
                                            }

                                            // Find selected subcategory in the current category
                                            const sc = subCategories.find((s) => s.id === selected);

                                            // If not found (e.g. category changed), show placeholder instead of MongoDB id
                                            if (!sc) {
                                                return <em style={{ color: '#9e9e9e' }}>Select subcategory</em>;
                                            }

                                            return sc.name;
                                        }
                                    }}
                                    sx={{
                                        '.MuiSelect-select': { py: 1.5 },
                                        '& .MuiOutlinedInput-root': { borderRadius: 2 }
                                    }}
                                >
                                    <MenuItem disabled value="">
                                        <em>Select subcategory</em>
                                    </MenuItem>

                                    {subCategories.map((sc) => (
                                        <MenuItem key={sc.id} value={sc.id}>
                                            {sc.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        )}

                        {/* STEP 3 */}
                        {step === 3 && (
                            <Box sx={{
                                width: '100%', px: '2px',
                                '@keyframes fadeSlideIn': {
                                    '0%': { opacity: 0, transform: 'translateY(14px)' },
                                    '100%': { opacity: 1, transform: 'translateY(0)' },
                                },
                                animation: 'fadeSlideIn 0.35s cubic-bezier(0.4, 0, 0.2, 1) both',
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                    Which orders is this about?
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#616161', mb: 3 }}>
                                    Pick one or more if the issue spans multiple orders. Helps us look up the right mailboxes, billing, and domains for your clients.
                                </Typography>

                                <Box sx={{ border: '1px solid #E0E0E0', borderRadius: 2, p: 2, mb: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox size="small" checked={notSpecificToOrder} onChange={handleNotSpecificChange} sx={{ color: '#E0E0E0', '&.Mui-checked': { color: 'rgba(74, 102, 241, 1)' } }} />
                                        }
                                        label={
                                            <Box>
                                                <Typography sx={{ fontWeight: 600, color: '#1a1a1a' }}>Not specific to an order</Typography>
                                                <Typography variant="body2" sx={{ color: '#616161', fontSize: '0.8rem' }}>Pick this if the issue doesn't relate to a particular order account access, general question, billing overall.</Typography>
                                            </Box>
                                        }
                                        sx={{ m: 0, alignItems: 'flex-start' }}
                                    />
                                </Box>

                                <Divider sx={{ my: 2, '&::before, &::after': { borderColor: '#E0E0E0' } }}>
                                    <Typography variant="caption" sx={{ color: '#9E9E9E', fontWeight: 600, letterSpacing: 0.5 }}>
                                        OR PICK ONE OR MORE ORDERS
                                    </Typography>
                                </Divider>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {orders.map((o) => (
                                        <Box key={o.orderNumber} sx={{ border: '1px solid #E0E0E0', borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center' }}>
                                            <Checkbox
                                                size="small"
                                                checked={selectedOrders.includes(o.orderNumber)}
                                                onChange={() => toggleOrder(o.orderNumber)}
                                                sx={{ color: '#E0E0E0', '&.Mui-checked': { color: 'rgba(74, 102, 241, 1)' } }}
                                            />
                                            <Box>
                                                <Typography sx={{ fontWeight: 600, color: '#1a1a1a' }}>{o.orderNumber} · {o.organisationName}</Typography>
                                                <Typography variant="body2" sx={{ color: '#616161', fontSize: '0.8rem' }}>
                                                    {o.mailboxCount} mailboxes · placed {o.orderDate}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* STEP 4 */}
                        {step === 4 && (
                            <Box sx={{
                                width: '100%', px: '2px',
                                '@keyframes fadeSlideIn': {
                                    '0%': { opacity: 0, transform: 'translateY(14px)' },
                                    '100%': { opacity: 1, transform: 'translateY(0)' },
                                },
                                animation: 'fadeSlideIn 0.35s cubic-bezier(0.4, 0, 0.2, 1) both',
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                    Describe your issue
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#616161', mb: 3 }}>
                                    Be specific what you were trying to do, what happened, what you expected. Add a screenshot or file if it helps.
                                </Typography>

                                {/* Summary Box */}
                                <Box sx={{ border: '1px solid #E0E0E0', borderRadius: 2, p: 2.5, mb: 3 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                        <Typography sx={{ color: '#616161', fontSize: '14px', width: '80px' }}>Category</Typography>
                                        <Typography sx={{ fontWeight: 600, color: '#1a1a1a', flex: 1, px: 2 }}>
                                            {issueCategories.find(c => c.id === category)?.name || category}
                                        </Typography>
                                        <Typography onClick={() => setStep(1)} sx={{ color: 'rgba(74, 102, 241, 1)', fontSize: '14px', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>Edit</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                        <Typography sx={{ color: '#616161', fontSize: '14px', width: '80px' }}>Issue</Typography>
                                        <Typography sx={{ fontWeight: 600, color: '#1a1a1a', flex: 1, px: 2 }}>
                                            {subCategories.find(s => s.id === issue)?.name || issue}
                                        </Typography>
                                        <Typography onClick={() => setStep(2)} sx={{ color: 'rgba(74, 102, 241, 1)', fontSize: '14px', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>Edit</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ color: '#616161', fontSize: '14px', width: '80px' }}>Order</Typography>
                                        <Typography sx={{ fontWeight: 600, color: '#1a1a1a', flex: 1, px: 2 }}>
                                            {getOrderLabel()}
                                        </Typography>
                                        <Typography onClick={() => setStep(3)} sx={{ color: 'rgba(74, 102, 241, 1)', fontSize: '14px', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>Edit</Typography>
                                    </Box>
                                </Box>

                                <TextareaAutosize
                                    minRows={4}
                                    maxRows={6}
                                    placeholder="Tell us what's going on..."
                                    style={{
                                        width: "100%",
                                        border: "1px solid #E0E0E0",
                                        borderRadius: 8,
                                        padding: 16,
                                        fontFamily: 'inherit',
                                        fontSize: 14,
                                        backgroundColor: '#FAFAFA',
                                        color: '#1a1a1a',
                                        resize: 'vertical',
                                        outline: 'none',
                                        marginBottom: '16px'
                                    }}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />

                                {/* Attachments */}
                                <Box>
                                    <Typography sx={{ fontWeight: 600, fontSize: '14px', mb: 1, color: '#1a1a1a' }}>
                                        Attachments <span style={{ color: '#757575', fontWeight: 400 }}>(optional)</span>
                                    </Typography>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        {attachments.map((item, idx) => (
                                            <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, border: "1px solid #E0E0E0", borderRadius: 2, bgcolor: "#FAFAFA" }}>
                                                <Avatar src={item.preview} variant="rounded" sx={{ width: 48, height: 48, borderRadius: 2 }} />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{item.file.name}</Typography>
                                                    <Typography variant="caption" sx={{ color: "#616161" }}>{(item.file.size / (1024 * 1024)).toFixed(2)} MB</Typography>
                                                </Box>
                                                <Button onClick={() => handleRemoveAttachment(idx)} size="small" sx={{ color: "#F44336" }}>Remove</Button>
                                            </Box>
                                        ))}

                                        <Button
                                            component="label"
                                            fullWidth
                                            sx={{
                                                textTransform: "none",
                                                border: "2px dashed #E0E0E0",
                                                borderRadius: 2,
                                                py: 2,
                                                display: 'flex',
                                                justifyContent: 'flex-start',
                                                gap: 2,
                                                color: '#1a1a1a',
                                                '&:hover': { bgcolor: '#FAFAFA', borderColor: '#BDBDBD' }
                                            }}
                                        >
                                            <UploadFileIcon sx={{ color: 'rgba(74, 102, 241, 1)', ml: 1, fontSize: 28 }} />
                                            <Box sx={{ textAlign: 'left' }}>
                                                <Typography sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                                                    Click to add {attachments.length > 0 ? 'more' : 'an'} attachment
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#757575' }}>Max 20 MB per file &middot; png, jpg, jpeg, csv, xlsx, pdf, svg</Typography>
                                            </Box>
                                            <input
                                                type="file"
                                                multiple
                                                accept=".png, .jpg, .jpeg, .csv, .xlsx, .pdf, .svg"
                                                hidden
                                                onChange={handleFileChange}
                                            />
                                        </Button>
                                    </Box>
                                </Box>

                                {/* Suggested Solutions */}
                                {suggestedSolutions.length > 0 && (
                                    <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(2, 136, 209, 0.04)', borderRadius: 2, border: '1px solid rgba(2, 136, 209, 0.15)' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <ArticleIcon sx={{ fontSize: 18, color: '#0288d1' }} />
                                            <Typography sx={{ fontWeight: 800, fontSize: '13px', color: '#0288d1', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Suggested solutions</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            {suggestedSolutions.map(art => (
                                                <Box
                                                    key={art.id || art._id}
                                                    onClick={() => {
                                                        setActive("Knowledge Base");
                                                        localStorage.setItem("sidebar-title", "Knowledge Base");
                                                        window.open(`${ROUTES.SUPPORT.HELP_GUIDES}?articleId=${art.id || art._id}`, '_blank')
                                                    }}
                                                    sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1.5, p: '10px 14px', bgcolor: 'white', borderRadius: '10px',
                                                        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)', cursor: 'pointer', transition: 'all 0.15s ease',
                                                        '&:hover': { bgcolor: '#FAFAFA', transform: 'translateY(-1px)', boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.05)' }
                                                    }}
                                                >
                                                    <DescriptionIcon sx={{ fontSize: 16, color: '#757575' }} />
                                                    <Typography sx={{ fontSize: '13px', flex: 1, fontWeight: 500, color: '#1a1a1a' }}>{art.title}</Typography>
                                                    <Typography sx={{ fontSize: '11px', color: '#757575' }}>
                                                        {art.readTime || '2 min'}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}

                    </Box>
                </Box>
            </DialogContent>

            {/* Navigation Actions */}
            <Box sx={{ display: "flex", justifyContent: step > 1 ? "space-between" : "flex-end", px: 3, py: 2.5, borderTop: "1px solid #E0E0E0", bgcolor: '#FAFAFA' }}>
                {step > 1 && (
                    <CustomButton
                        onClick={() => setStep(step - 1)}
                        variant="outlined"
                        color="primary"
                        sx={{
                            px: 3,
                        }}
                    >
                        Back
                    </CustomButton>
                )}

                {step < 4 ? (
                    <CustomButton
                        onClick={() => setStep(step + 1)}
                        disabled={(step === 1 && !isStep1Valid) || (step === 2 && !isStep2Valid) || (step === 3 && !isStep3Valid)}
                        variant="contained"
                        color="primary"
                        sx={{
                            px: 4,
                        }}
                    >
                        Next
                    </CustomButton>
                ) : (
                    <CustomButton
                        onClick={handleSubmit}
                        disabled={!isStep4Valid || loading}
                        variant="contained"
                        color="primary"
                        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                        sx={{
                            px: 4,
                        }}
                    >
                        {loading ? 'Submitting...' : 'Submit ticket'}
                    </CustomButton>
                )}
            </Box>
        </Dialog>
    );
};

export default CreateTicketModal;
